import React, { useMemo } from 'react';
import { PdfFieldEntity } from '../data/PdfDataKey';
import { PdfFieldResolving } from '../data/PdfFieldResolving';
import { PdfComponentType, PdfTableComponent, PdfRepeat } from '../PdfComponent';
import { PdfEditorPreviewPage, usePdfTemplateContext } from '../PdfTemplateEditorContext';

// using shared pdfFontToCss from pdfTemplate

export default function PdfTemplateTablePreview<T>({ component }: { component: PdfTableComponent }) {
  const {
    fontToCss,
    tableColumnWidthsPx,
    mmToPxScaled,
    tableProviders,
    selectedEntity,
    documentProperties,
    components,
    editorSettings,
    data,
  } = usePdfTemplateContext<T>();
  const cssFont = fontToCss(component.font ?? documentProperties.font);
  const rowHeightPx = mmToPxScaled(component.rowHeight);
  const headerPx = component.header ? rowHeightPx : 0;

  const widthPx = mmToPxScaled(component.width);
  const heightPx = mmToPxScaled(component.height);

  const availableRowsPx = Math.max(0, heightPx - headerPx);
  const rowsPerPage = Math.max(1, Math.floor(availableRowsPx / rowHeightPx));

  const scaledWidthsPx = tableColumnWidthsPx(component);
  let accLeft = 0;
  const colPositions = scaledWidthsPx.map(w => {
    const pos = { left: accLeft, width: w };
    accLeft += w;
    return pos;
  });

  const provider = useMemo(
    () => tableProviders.find(p => p.tableType === component.tableType),
    [tableProviders, component.tableType],
  );

  const resolvedRows: any[] = useMemo(() => {
    if (!selectedEntity || !provider) return [];
    return provider.rowResolver(selectedEntity);
  }, [tableProviders, selectedEntity]);

  // Compute reserved header/footer bands on the previewed page
  const preview = editorSettings.previewPage;
  const appliesToPreview = (mode: PdfRepeat) => {
    switch (mode) {
      case PdfRepeat.always:
        return true;
      case PdfRepeat.first:
        return preview === PdfEditorPreviewPage.first;
      case PdfRepeat.last:
        return preview === PdfEditorPreviewPage.last;
    }
  };

  // Build intervals in mm for components that apply (non-table)
  const intervalsMm = components
    .filter(c => c.type !== PdfComponentType.table)
    .filter(c => appliesToPreview(c.repeatMode))
    .map(c => ({ start: c.y, end: c.y + c.height }))
    .sort((a, b) => a.start - b.start);

  let startYMm = component.y;
  for (const interval of intervalsMm) {
    if (startYMm < interval.end && startYMm >= interval.start) startYMm = interval.end;
  }

  let endLimitMm = Number.POSITIVE_INFINITY; // Clamp to component.height below
  for (const interval of intervalsMm) {
    if (interval.start >= startYMm) {
      endLimitMm = Math.min(endLimitMm, interval.start);
      break;
    }
  }

  const bottomMm = Math.min(component.y + component.height, endLimitMm);
  const availableHeightMm = Math.max(0, bottomMm - startYMm);
  const availableHeightPx = mmToPxScaled(availableHeightMm);

  const topOffsetPx = mmToPxScaled(Math.max(0, startYMm - component.y));

  // Recompute rows based on adjusted height for preview
  const rows = Array.from(
    { length: Math.max(1, Math.floor(Math.max(0, availableHeightPx - headerPx) / rowHeightPx)) },
    (_, i) => i,
  );
  const borderColor = component.border.color;
  const borderWidthPx = component.border.width;

  return (
    <div className='relative h-full w-full overflow-hidden' style={cssFont}>
      <div
        className='absolute left-0'
        style={{
          top: topOffsetPx,
          width: widthPx,
          height: availableHeightPx,
          borderStyle: 'solid',
          borderColor: borderColor,
          borderWidth: borderWidthPx,
          pointerEvents: 'none',
          boxSizing: 'border-box',
          zIndex: 1,
        }}
      />

      {/* HEADER */}
      {component.header && (
        <div
          className='absolute left-0 flex w-full bg-white font-medium'
          style={{
            top: topOffsetPx,
            height: `${headerPx}px`,
            borderBottomStyle: 'solid',
            borderBottomColor: borderColor,
            borderBottomWidth: borderWidthPx,
          }}
        >
          {component.columns.map((c, i) => (
            <div
              key={c.id}
              className='flex items-center px-2'
              style={{
                width: colPositions[i].width,
                left: colPositions[i].left,
                borderRightStyle: component.borderCellsVertical ? 'solid' : undefined,
                borderRightColor: component.borderCellsVertical ? borderColor : undefined,
                borderRightWidth: component.borderCellsVertical ? borderWidthPx : undefined,
              }}
            >
              <span
                className='w-full truncate'
                style={{
                  textAlign: c.align,
                  ...cssFont,
                }}
              >
                {c.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* BODY  */}
      <div className='absolute left-0 w-full' style={{ top: topOffsetPx + headerPx }}>
        {rows.map(i => (
          <div
            key={i}
            className={`flex w-full bg-white`}
            style={{
              height: rowHeightPx,
              borderBottomStyle: component.borderCellsHorizontal ? 'solid' : undefined,
              borderBottomColor: component.borderCellsHorizontal ? borderColor : undefined,
              borderBottomWidth: component.borderCellsHorizontal ? borderWidthPx : undefined,
            }}
          >
            {component.columns.map((c, idx) => (
              <div
                key={`${c.id}-${i}`}
                className={`flex items-center px-2 ${component.borderCellsVertical ? 'last:border-r-0' : ''}`}
                style={{
                  width: colPositions[idx].width,
                  textAlign: c.align,
                  ...cssFont,
                  borderRightStyle: component.borderCellsVertical ? 'solid' : undefined,
                  borderRightColor: component.borderCellsVertical ? borderColor : undefined,
                  borderRightWidth: component.borderCellsVertical ? borderWidthPx : undefined,
                }}
              >
                {resolvedRows[i] && (
                  <span className='w-full truncate' style={cssFont}>
                    {!c.key
                      ? ''
                      : PdfFieldResolving.getValue(resolvedRows[i], PdfFieldEntity.productTransaction, c.key, data)}
                  </span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
